import { Component, Input, ViewEncapsulation } from '@angular/core';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { OrderDetailService } from '@cutting/main/order/order-detail/order-detail.service';
import {
  BazisMaterialHelper,
  CuttingButt,
  CuttingSettings,
  ICustomResponse,
  MaterialBaseButtsDialogComponent,
  MaterialBaseEdgeDialogData,
  NoDataDisplayComponent,
  PriceListProductElementEdge,
} from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Observable } from 'rxjs';
import { filter, finalize, map, tap } from 'rxjs/operators';

import { AsyncPipe, NgIf } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { EdgeMaterialsDialogComponent } from './edge-materials-dialog/edge-materials-dialog.component';

@Component({
  templateUrl: './product-element-edge-dialog.component.html',
  styleUrls: ['./product-element-edge-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatToolbarModule,
    MatDividerModule,
    MatTooltipModule,
    MatDialogModule,
    NoDataDisplayComponent,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class ProductElementEdgeDialogComponent {
  @Input() cuttingOptions: CuttingSettings;

  buttsInColorList: CuttingButt[] = [];

  matched$: Observable<ICustomResponse<CuttingButt[]>>;
  select$: Observable<CuttingButt[]>;

  displayColumns = ['originalMaterial', 'newMaterial', 'change'];
  dataSource: MatTableDataSource<PriceListProductElementEdge> = new MatTableDataSource();

  matchedEdgesDialogRef: MatDialogRef<EdgeMaterialsDialogComponent>;
  materialBaseButtsDialogRef: MatDialogRef<MaterialBaseButtsDialogComponent>;

  @Input() set edges(val: PriceListProductElementEdge[]) {
    this.dataSource.data = _.cloneDeep(val);
  }
  get edges(): PriceListProductElementEdge[] {
    return this.dataSource.data;
  }

  @Input() set materialName(val: string) {
    this.getMatchedButts(val);
  }

  constructor(
    private orderDetailService: OrderDetailService,
    public dialogRef: MatDialogRef<ProductElementEdgeDialogComponent>,
    private dialog: MatDialog
  ) {}

  openMatchedEdgesDialog(item: PriceListProductElementEdge): void {
    this.matchedEdgesDialogRef = this.dialog.open(EdgeMaterialsDialogComponent, {
      panelClass: 'matched-edges-dialog',
      disableClose: false,
      width: '700px',
    });

    this.matchedEdgesDialogRef.componentInstance.edges = this.getMatchedEdgesByThickness(item.originalThickness);
    this.matchedEdgesDialogRef.componentInstance.cuttingOptions = this.cuttingOptions;
    this.matchedEdgesDialogRef.componentInstance.originalThickness = item.originalThickness;

    this.select$ = this.matchedEdgesDialogRef.afterClosed().pipe(
      filter(result => !!result),
      tap((result: CuttingButt[]) => {
        if (result && result.length > 0) {
          item.name = BazisMaterialHelper.increaseMaterial(result[0].name, result[0].article);
          item.thickness = result[0].thickness;
          item.isTape = result[0].isTape;
          item.sign = result[0].sign;
        }
      }),
      finalize(() => (this.matchedEdgesDialogRef = null))
    );
  }

  openMaterialBaseDialog(item: PriceListProductElementEdge): void {
    this.materialBaseButtsDialogRef = this.dialog.open(MaterialBaseButtsDialogComponent, {
      panelClass: 'material-base-butt-list-dialog',
      disableClose: false,
      width: '1000px',
      data: {
        filterByThickness: item.originalThickness,
        showQuantityInStock: this.cuttingOptions.showQuantityInStock,
      } as MaterialBaseEdgeDialogData,
    });

    this.select$ = this.materialBaseButtsDialogRef.afterClosed().pipe(
      tap((result: CuttingButt | undefined) => {
        if (result) {
          item.name = BazisMaterialHelper.increaseMaterial(result.name, result.article);
          item.thickness = result.thickness;
          item.isTape = result.isTape;
          item.sign = result.sign;
        }
      }),
      map(res => [res]),
      finalize(() => (this.materialBaseButtsDialogRef = null))
    );
  }

  getMatchedEdgesByThickness(thisckness: number): CuttingButt[] {
    return this.buttsInColorList.filter(b => b.thickness === thisckness);
  }

  getNewEdgeName = (data: PriceListProductElementEdge): string => this.getEdgeName(data.name, data.thickness);

  getOldEdgeName = (data: PriceListProductElementEdge): string =>
    this.getEdgeName(data.originalName, data.originalThickness);

  private getMatchedButts(materialName: string): void {
    this.matched$ = this.orderDetailService.getMatchedButts(materialName).pipe(
      filter(response => !!response.result),
      tap(response => {
        this.buttsInColorList = response.result;

        const data = this.dataSource.data;

        data.forEach(e => {
          if (!e.name) {
            const inMatched = this.buttsInColorList.find(b => b.thickness === e.originalThickness);

            if (inMatched) {
              e.name = BazisMaterialHelper.increaseMaterial(inMatched.name, inMatched.article);
              e.thickness = inMatched.thickness;
              e.sign = inMatched.sign;
            }
          }
        });
      })
    );
  }

  private getEdgeName(name: string, thickness: number): string {
    return name
      ? `${BazisMaterialHelper.formatMaterialName(name)} (${thickness} ${$localize`мм`})`
      : $localize`Не задана`;
  }
}
